import { supabase } from '@/integrations/supabase/client';
import { Session, User } from '@supabase/supabase-js';

export interface AuthValidationResult {
  isValid: boolean;
  session: Session | null;
  user: User | null;
  error?: string;
  dbValidated?: boolean;
}

export interface DatabaseAuthValidationResult {
  isValid: boolean;
  userId?: string;
  dbValidated?: boolean;
  sessionValid?: boolean;
  error?: string;
  details?: string;
}

/**
 * Validates auth from database perspective using edge function
 */
export const validateDatabaseAuth = async (): Promise<DatabaseAuthValidationResult> => {
  try {
    console.log('🔍 Validating database auth...');
    
    const { data, error } = await supabase.functions.invoke('auth-validator');
    
    if (error) {
      console.error('❌ Database auth validation error:', error);
      return {
        isValid: false,
        error: 'Database auth validation failed',
        details: error.message
      };
    }

    console.log('📊 Database auth validation result:', data);
    
    return {
      isValid: data?.isValid || false,
      userId: data?.userId,
      dbValidated: data?.dbValidated,
      sessionValid: data?.sessionValid,
      error: data?.error,
      details: data?.details
    };
  } catch (error: any) {
    console.error('🚨 Database auth validation error:', error);
    return {
      isValid: false,
      error: 'Database auth validation failed',
      details: error.message
    };
  }
};

/**
 * Validates the current session and ensures auth.uid() will work in database operations
 */
export const validateAuthSession = async (): Promise<AuthValidationResult> => {
  try {
    // Get the current session
    const { data: { session }, error: sessionError } = await supabase.auth.getSession();
    
    if (sessionError) {
      console.error('Session validation error:', sessionError);
      return {
        isValid: false,
        session: null,
        user: null,
        error: 'Failed to validate session'
      };
    }

    if (!session || !session.user) {
      return {
        isValid: false,
        session: null,
        user: null,
        error: 'No active session found'
      };
    }

    // Check if token is expired or about to expire (within 5 minutes)
    const expiresAt = session.expires_at;
    const now = Math.floor(Date.now() / 1000);
    const fiveMinutesFromNow = now + (5 * 60);

    if (expiresAt && expiresAt < fiveMinutesFromNow) {
      console.log('Token is expired or expiring soon, attempting refresh...');
      
      // Attempt to refresh the session
      const { data: { session: refreshedSession }, error: refreshError } = await supabase.auth.refreshSession();
      
      if (refreshError || !refreshedSession) {
        console.error('Session refresh failed:', refreshError);
        return {
          isValid: false,
          session: null,
          user: null,
          error: 'Session expired and refresh failed'
        };
      }

      return {
        isValid: true,
        session: refreshedSession,
        user: refreshedSession.user,
      };
    }

    // Verify the user can be retrieved (this tests if auth.uid() will work)
    const { data: { user }, error: userError } = await supabase.auth.getUser();
    
    if (userError || !user) {
      console.error('User verification failed:', userError);
      return {
        isValid: false,
        session: null,
        user: null,
        error: 'User authentication failed'
      };
    }

    // Also validate database-level auth
    const dbAuthResult = await validateDatabaseAuth();
    
    return {
      isValid: true,
      session,
      user,
      dbValidated: dbAuthResult.isValid
    };
  } catch (error) {
    console.error('Auth validation error:', error);
    return {
      isValid: false,
      session: null,
      user: null,
      error: 'Authentication validation failed'
    };
  }
};

/**
 * Refreshes the current session if needed
 */
export const refreshSessionIfNeeded = async (): Promise<{ success: boolean; session?: Session; error?: string }> => {
  try {
    const { data: { session }, error } = await supabase.auth.refreshSession();
    
    if (error) {
      console.error('Session refresh failed:', error);
      return { success: false, error: error.message };
    }

    return { success: true, session: session || undefined };
  } catch (error) {
    console.error('Session refresh error:', error);
    return { success: false, error: 'Failed to refresh session' };
  }
};

/**
 * Forces a re-authentication flow
 */
export const requireReauth = async (): Promise<{ success: boolean; error?: string }> => {
  try {
    // Sign out and require fresh login
    await supabase.auth.signOut();
    return { success: true };
  } catch (error) {
    console.error('Re-auth error:', error);
    return { success: false, error: 'Failed to initiate re-authentication' };
  }
};

/**
 * Ensures the user has a valid profile in the database
 */
export const ensureUserProfile = async (user: User): Promise<void> => {
  try {
    // Check if profile exists
    const { data: existingProfile, error: profileError } = await supabase
      .from('profiles')
      .select('id')
      .eq('user_id', user.id)
      .single();

    if (profileError && profileError.code !== 'PGRST116') {
      console.error('Profile check error:', profileError);
      throw new Error('Failed to verify user profile');
    }

    if (!existingProfile) {
      // Create profile if it doesn't exist
      const { error: insertError } = await supabase
        .from('profiles')
        .insert({
          user_id: user.id,
          display_name: user.user_metadata?.display_name || user.email?.split('@')[0] || 'User'
        });

      if (insertError) {
        console.error('Profile creation error:', insertError);
        throw new Error('Failed to create user profile');
      }
    }
  } catch (error) {
    console.error('Profile setup error:', error);
    throw error;
  }
};

/**
 * Debug authentication state
 */
export const debugAuthState = async (): Promise<void> => {
  try {
    console.group('🔍 Auth State Debug');
    
    const { data: { session }, error: sessionError } = await supabase.auth.getSession();
    console.log('Session:', session);
    console.log('Session Error:', sessionError);
    
    if (session) {
      console.log('User ID:', session.user.id);
      console.log('Token expires at:', new Date(session.expires_at! * 1000));
      console.log('Time until expiry:', (session.expires_at! - Math.floor(Date.now() / 1000)) / 60, 'minutes');
    }
    
    const { data: { user }, error: userError } = await supabase.auth.getUser();
    console.log('Current User:', user);
    console.log('User Error:', userError);
    
    // Test database auth
    const dbAuthResult = await validateDatabaseAuth();
    console.log('Database Auth Result:', dbAuthResult);
    
    console.groupEnd();
  } catch (error) {
    console.error('Debug auth state error:', error);
  }
};

/**
 * Comprehensive auth retry mechanism with automatic token refresh
 */
export const withAuthRetry = async <T>(
  operation: () => Promise<T>,
  maxRetries: number = 2
): Promise<T> => {
  let lastError: any;
  
  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      console.log(`🔄 Auth operation attempt ${attempt}/${maxRetries}`);
      
      // Validate auth before operation
      const authValidation = await validateAuthSession();
      if (!authValidation.isValid) {
        console.log('❌ Auth validation failed, attempting refresh...');
        
        // Try to refresh session
        const refreshResult = await refreshSessionIfNeeded();
        if (!refreshResult.success) {
          throw new Error('Session refresh failed: ' + refreshResult.error);
        }
        
        // Revalidate after refresh
        const revalidation = await validateAuthSession();
        if (!revalidation.isValid) {
          throw new Error('Auth still invalid after refresh');
        }
      }
      
      // Execute operation
      const result = await operation();
      console.log('✅ Auth operation successful');
      return result;
      
    } catch (error: any) {
      lastError = error;
      console.log(`❌ Auth operation attempt ${attempt} failed:`, error);
      
      if (attempt === maxRetries) {
        break;
      }
      
      // If auth-related error, try to refresh session before retry
      if (error.message?.includes('auth') || error.message?.includes('session') || 
          error.message?.includes('token') || error.message?.includes('policy')) {
        console.log('🔄 Auth error detected, refreshing session before retry...');
        await refreshSessionIfNeeded();
        
        // Small delay before retry
        await new Promise(resolve => setTimeout(resolve, 1000));
      } else {
        // Non-auth error, don't retry
        break;
      }
    }
  }
  
  throw lastError;
};